import { ThemeProvider } from '@emotion/react';
import is from '@sindresorhus/is';
import {
    type ColumnDef,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    type Row,
    type SortingState,
    useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';
import { format, formatDistance } from 'date-fns';
import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import type { ChangeEvent, FunctionComponent, ReactNode, UIEvent } from 'react';
import { memo, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { ApplicationContext } from '~/shared/context/context';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { GeneralFilter } from '~/shared/data-contract/filter';
import { and, contains, equals } from '~/shared/data-contract/filter';
import type {
    FilterState,
    FilterStateDate,
    FilterStateOption,
    FilterWithFilterStates,
} from '~/shared/data-contract/filter-state';
import type { TranslationKey } from '~/shared/internationalization/translation';
import { Badge, type BadgeProps } from '~/ui/core/badge/Badge';
import { Button } from '~/ui/core/button/Button';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuSubTriggerContent,
    DropdownMenuTrigger,
} from '~/ui/core/dropdown-menu/DropdownMenu';
import {
    IconChevronRightSmall,
    IconCrossSmall,
    IconFilter,
    IconPlus,
    IconSearch,
    IconSortArrow,
} from '~/ui/core/icons';
import { Input } from '~/ui/core/input/Input';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { Skeleton } from '~/ui/core/skeleton/Skeleton';
import { Spinner } from '~/ui/core/spinner/Spinner';
import {
    TableBody,
    TableCell,
    type TableColumnWidth,
    TableContainer,
    TableErrorMessage,
    TableGrid,
    TableHeader,
    TableHeaderCell,
    TableRow,
    TableSkeletonRow,
} from '~/ui/core/table/Table';
import { TileTitle } from '~/ui/core/tile/Tile';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';
import { BoardContentWithHeader } from '~/ui/domain/board/BoardContentWithHeader';
import { OutlinedInputWrapper, SearchInput } from '~/ui/domain/search-input/SearchInput';
import { TilingEngine } from '~/ui/domain/tiling-engine/TilingEngine';
import { theme } from '~/ui/theme/cobrainer-theme';

import { convertFilterStateToFilters } from '../app/common/filter-sidebar/utils';
import { Error403 } from '../app/error-handling/ErrorPages';
import { useSnackbars } from '../app/error-handling/SnackBars';
import { useManySkillsQuery, useSearchSkillsQuery } from '../app/query-hooks/skills';
import { Row as BoardHeaderRow, BoardHeaderWrapper, ConfirmProvider, RightSide } from '../job-architecture-v2';
import { createSkillDetailTilePayload } from '../skills';
import { useUrlTiles } from '../url-tiles';
import {
    useLearningOpportunitiesPermissions,
    useLearningOpportunityFilter,
    useLearningOpportunityFilterQuery,
    useUpdateLearningOpportunityMutation,
} from './api';
import { route } from './constants';
import type { LearningOpportunity, LearningOpportunityState } from './data-contract';
import {
    buildLearningOpportunityUpdatePayload,
    type EditableLearningOpportunityState,
    editableLearningOpportunityStates,
    stateTranslationKey,
} from './helpers';
import { LearningOpportunitySidepanel } from './LearningOpportunitySidepanel';
import { useInvalidateLearningOpportunityRelatedCaches } from './use-invalidate-related-caches';

const TABLE_PAGE_SIZE = 50;
const ROW_HEIGHT = 56;
const SKELETON_ROW_COUNT = 5;
const SEARCH_DEBOUNCE_MS = 300;
const TABLE_COLUMNS: readonly TableColumnWidth[] = [
    'minmax(280px,1.6fr)',
    'minmax(120px,0.7fr)',
    'minmax(120px,0.7fr)',
    'minmax(120px,0.7fr)',
    'minmax(120px,0.7fr)',
    'minmax(120px,0.7fr)',
];
const DATA_COLUMNS = ['title', 'updatedAt', 'provider', 'state', 'ownerName', 'createdAt'] as const;
const learningOpportunityStates: readonly LearningOpportunityState[] = ['all', 'draft', 'published', 'archived'];

const combineFilters = (...filters: readonly (GeneralFilter | undefined)[]): GeneralFilter | undefined => {
    const definedFilters = filters.filter((filter): filter is GeneralFilter => filter !== undefined);

    if (definedFilters.length === 0) return undefined;
    if (definedFilters.length === 1) return definedFilters[0];

    return and<Record<string, Date | boolean | number | string | readonly string[]>>(...definedFilters);
};

const getStateFilter = (state: LearningOpportunityState): GeneralFilter | undefined =>
    state === 'all' ? undefined : (equals('state', state) as GeneralFilter);

const getSearchFilter = (searchTerm: string): GeneralFilter | undefined =>
    is.nonEmptyString(searchTerm) ? (contains('title', searchTerm, true) as GeneralFilter) : undefined;

type SkillLabelMap = Readonly<Record<string, string>>;

const useDebouncedSearch = (): {
    readonly inputValue: string;
    readonly searchTerm: string;
    readonly setInputValue: (event: ChangeEvent<HTMLInputElement>) => void;
} => {
    const [inputValue, updateInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const debounced = useMemo(() => debounce((value: string) => setSearchTerm(value.trim()), SEARCH_DEBOUNCE_MS), []);

    useEffect(() => () => debounced.cancel(), [debounced]);

    const setInputValue = (event: ChangeEvent<HTMLInputElement>): void => {
        const value = event.target.value;
        updateInputValue(value);
        debounced(value);
    };

    return { inputValue, searchTerm, setInputValue };
};

const useLearningOpportunitiesInfiniteTable = ({
    baseFilter,
    searchTerm,
    sort,
    state,
}: {
    readonly baseFilter?: GeneralFilter;
    readonly searchTerm: string;
    readonly sort: SortingState;
    readonly state: LearningOpportunityState;
}): {
    readonly error: Error | null;
    readonly fetchNextPage: () => void;
    readonly hasNextPage: boolean;
    readonly isFetching: boolean;
    readonly isLoading: boolean;
    readonly opportunities: readonly LearningOpportunity[];
    readonly totalCount: number;
} => {
    const [page, setPage] = useState(0);
    const [opportunities, setOpportunities] = useState<readonly LearningOpportunity[]>([]);
    const filter = useMemo(
        () => combineFilters(baseFilter, getSearchFilter(searchTerm), getStateFilter(state)),
        [baseFilter, searchTerm, state],
    );
    const sorted = useMemo(
        () => ({
            fields: sort.map((item) => ({
                direction: item.desc ? ('desc' as const) : ('asc' as const),
                field: item.id,
            })),
        }),
        [sort],
    );
    const resetKey = useMemo(() => JSON.stringify({ filter, sorted }), [filter, sorted]);
    const query = useLearningOpportunityFilterQuery({
        filter,
        paged: { page, size: TABLE_PAGE_SIZE },
        sorted,
    });
    const totalCount = query.isPlaceholderData ? opportunities.length : (query.data?.pagination.count ?? 0);
    const hasNextPage = opportunities.length < totalCount;

    useEffect(() => {
        void resetKey;
        setPage(0);
        setOpportunities([]);
    }, [resetKey]);

    useEffect(() => {
        if (query.isPlaceholderData) return;

        const pageData = query.data?.data;
        if (pageData === undefined) return;

        setOpportunities((current) => {
            if (page === 0) return pageData;

            const existingIds = new Set(current.map((item) => item.id));
            const nextItems = pageData.filter((item) => !existingIds.has(item.id));

            return [...current, ...nextItems];
        });
    }, [page, query.data, query.isPlaceholderData]);

    const fetchNextPage = (): void => {
        if (query.isFetching || hasNextPage === false) return;
        setPage((current) => current + 1);
    };

    return {
        error: query.error,
        fetchNextPage,
        hasNextPage,
        isFetching: query.isFetching,
        isLoading: query.isPending && page === 0,
        opportunities,
        totalCount,
    };
};

const SortButton: FunctionComponent<{
    readonly children: ReactNode;
    readonly columnId: string;
    readonly sorting: SortingState;
    readonly onSortingChange: (sorting: SortingState) => void;
}> = ({ children, columnId, onSortingChange, sorting }) => {
    const active = sorting.find((item) => item.id === columnId);

    return (
        <button
            className="flex appearance-none items-center gap-xs border-0 bg-transparent p-0 text-left font-inherit text-gray-800"
            type="button"
            onClick={() => onSortingChange([{ id: columnId, desc: active?.desc === false }])}
        >
            {children}
            {active !== undefined && (
                <IconSortArrow size={14} style={{ transform: active.desc ? 'rotate(180deg)' : undefined }} />
            )}
        </button>
    );
};

const TranslatedHeader: FunctionComponent<{
    readonly columnId: string;
    readonly label: TranslationKey;
    readonly sorting: SortingState;
    readonly onSortingChange: (sorting: SortingState) => void;
}> = ({ columnId, label, onSortingChange, sorting }) => {
    const translate = useTranslate();

    return (
        <SortButton columnId={columnId} sorting={sorting} onSortingChange={onSortingChange}>
            {translate(label)}
        </SortButton>
    );
};

type LearningOpportunityRow = LearningOpportunity;

const stateBadgeVariant = (state: LearningOpportunityState): NonNullable<BadgeProps['variant']> =>
    state === 'published' ? 'success' : state === 'archived' ? 'error' : 'gray';

const StateBadgeDropdown: FunctionComponent<{ readonly opportunity: LearningOpportunity }> = ({ opportunity }) => {
    const translate = useTranslate();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbars();
    const updateMutation = useUpdateLearningOpportunityMutation('patchJson', opportunity.id);
    const invalidateRelatedCaches = useInvalidateLearningOpportunityRelatedCaches();

    const handleSelect = (next: EditableLearningOpportunityState): void => {
        if (next === opportunity.state || updateMutation.isPending) return;
        updateMutation.mutate(buildLearningOpportunityUpdatePayload(opportunity, next), {
            onError: showErrorSnackbar,
            onSuccess: () => {
                showSuccessSnackbar('success.learning-opportunities.edited');
                invalidateRelatedCaches();
            },
        });
    };

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild={true}>
                <button
                    aria-label={translate('pages.learning-opportunities.headlines.state')}
                    className="appearance-none border-0 bg-transparent p-0"
                    disabled={updateMutation.isPending}
                    type="button"
                    onClick={(event) => event.stopPropagation()}
                >
                    <Badge variant={stateBadgeVariant(opportunity.state)}>
                        {translate(stateTranslationKey(opportunity.state))}
                    </Badge>
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
                {editableLearningOpportunityStates.map((stateOption) => (
                    <DropdownMenuItem
                        key={stateOption}
                        disabled={stateOption === opportunity.state}
                        onSelect={() => handleSelect(stateOption)}
                    >
                        {translate(stateTranslationKey(stateOption))}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

const DateCell: FunctionComponent<{ readonly value?: string }> = ({ value }) => {
    const {
        applicationState: { clock, language },
    } = useApplicationContext();

    if (!is.nonEmptyString(value)) return <span>–</span>;

    return (
        <span>
            {formatDistance(new Date(value), clock.getCurrentTimestamp(), {
                addSuffix: true,
                locale: language.date.locale,
            })}
        </span>
    );
};

const createLearningOpportunityColumns = ({
    onSortingChange,
    sorting,
    translateProvider,
}: {
    readonly onSortingChange: (sorting: SortingState) => void;
    readonly sorting: SortingState;
    readonly translateProvider: (provider: string | undefined) => string;
}): readonly ColumnDef<LearningOpportunityRow>[] => [
    {
        accessorKey: 'title',
        cell: ({ row }) => <span className="min-w-0 truncate text-black-900">{row.original.title}</span>,
        header: () => (
            <TranslatedHeader
                columnId="title"
                label="pages.learning-opportunities.headlines.title"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
    {
        accessorKey: 'updatedAt',
        cell: ({ row }) => <DateCell value={row.original.updatedAt} />,
        header: () => (
            <TranslatedHeader
                columnId="updatedAt"
                label="pages.learning-opportunities.headlines.updated-at"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
    {
        accessorKey: 'provider',
        cell: ({ row }) => <span className="truncate">{translateProvider(row.original.provider)}</span>,
        header: () => (
            <TranslatedHeader
                columnId="provider"
                label="pages.learning-opportunities.headlines.provider"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
    {
        accessorKey: 'state',
        cell: ({ row }) => <StateBadgeDropdown opportunity={row.original} />,
        header: () => (
            <TranslatedHeader
                columnId="state"
                label="pages.learning-opportunities.headlines.state"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
    {
        accessorKey: 'ownerName',
        cell: ({ row }) => <span className="truncate">{row.original.ownerName ?? '–'}</span>,
        header: () => (
            <TranslatedHeader
                columnId="ownerName"
                label="pages.learning-opportunities.headlines.owner"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
    {
        accessorKey: 'createdAt',
        cell: ({ row }) => <DateCell value={row.original.createdAt} />,
        header: () => (
            <TranslatedHeader
                columnId="createdAt"
                label="pages.learning-opportunities.headlines.created"
                sorting={sorting}
                onSortingChange={onSortingChange}
            />
        ),
    },
];

const VirtualLearningOpportunityRow: FunctionComponent<{
    readonly onSelect: (id: string) => void;
    readonly row: Row<LearningOpportunityRow>;
    readonly virtualRow: VirtualItem;
}> = memo(({ onSelect, row, virtualRow }) => {
    const handleSelect = (): void => onSelect(row.original.id);

    return (
        <div className="absolute left-0 top-0 w-full" style={{ transform: `translateY(${virtualRow.start}px)` }}>
            <TableRow
                $columnWidths={TABLE_COLUMNS}
                className="cursor-pointer"
                role="button"
                style={{ marginTop: 0 }}
                tabIndex={0}
                onClick={handleSelect}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelect();
                    }
                }}
            >
                {row.getVisibleCells().map((cell, cellIndex) => (
                    <TableCell key={cell.id} data-column={DATA_COLUMNS[cellIndex]} role="gridcell">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                ))}
            </TableRow>
        </div>
    );
});

const LearningOpportunitiesTable: FunctionComponent<{
    readonly filter?: GeneralFilter;
    readonly searchTerm: string;
    readonly state: LearningOpportunityState;
}> = ({ filter, searchTerm, state }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const [sorting, setSorting] = useState<SortingState>([{ id: 'updatedAt', desc: true }]);
    const { error, fetchNextPage, hasNextPage, isFetching, isLoading, opportunities } =
        useLearningOpportunitiesInfiniteTable({
            baseFilter: filter,
            searchTerm,
            sort: sorting,
            state,
        });
    const {
        applicationState: { language },
        translate: translateFromContext,
    } = useContext(ApplicationContext);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const columns = useMemo(
        () =>
            createLearningOpportunityColumns({
                onSortingChange: setSorting,
                sorting,
                translateProvider: (provider) =>
                    is.nonEmptyString(provider) ? translateFromContext(`providers.${provider}` as TranslationKey) : '–',
            }),
        [sorting, translateFromContext],
    );
    const handleRowSelect = useCallback(
        (id: string): void => {
            navigate(`${route.patterns.learningOpportunities}/${id}`);
        },
        [navigate],
    );
    const table = useReactTable({
        columns: columns as ColumnDef<LearningOpportunityRow>[],
        data: opportunities as LearningOpportunityRow[],
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id,
        getSortedRowModel: getSortedRowModel(),
        manualSorting: true,
        onSortingChange: setSorting,
        state: { sorting },
    });
    const rows = table.getRowModel().rows;
    const isFetchingMore = isFetching && isLoading === false;
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        estimateSize: () => ROW_HEIGHT,
        getScrollElement: () => scrollContainerRef.current,
        overscan: 5,
    });

    const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
        const target = event.currentTarget;
        const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 160;

        if (isNearBottom && hasNextPage && isFetching === false) fetchNextPage();
    };

    if (error !== null) {
        return (
            <TableContainer>
                <TableErrorMessage>{error.message}</TableErrorMessage>
            </TableContainer>
        );
    }

    return (
        <TableContainer>
            <TableGrid aria-label="Learning opportunities table" role="grid">
                <TableHeader $columnWidths={TABLE_COLUMNS} role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                        headerGroup.headers.map((header, index) => (
                            <TableHeaderCell key={header.id} data-column={DATA_COLUMNS[index]} role="columnheader">
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </TableHeaderCell>
                        )),
                    )}
                </TableHeader>
                <TableBody
                    ref={scrollContainerRef}
                    data-testid="learning-opportunities-table-body"
                    onScroll={handleScroll}
                >
                    {isLoading ? (
                        Array.from({ length: 20 }).map((_, index) => (
                            <TableSkeletonRow
                                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders.
                                key={index}
                                columnCount={TABLE_COLUMNS.length}
                                columnWidths={TABLE_COLUMNS}
                            />
                        ))
                    ) : rows.length === 0 ? (
                        <div className="p-xl text-center text-gray-700 text-sm">
                            {translate('pages.global-search.no-data.learning-opportunities' as TranslationKey)}
                        </div>
                    ) : (
                        <div
                            style={{
                                height: `${
                                    rowVirtualizer.getTotalSize() +
                                    (isFetchingMore ? SKELETON_ROW_COUNT * ROW_HEIGHT : 0)
                                }px`,
                                position: 'relative',
                                width: '100%',
                            }}
                        >
                            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                                const row = rows[virtualRow.index];

                                return row === undefined ? null : (
                                    <VirtualLearningOpportunityRow
                                        key={`${row.id}-${language.code}`}
                                        row={row}
                                        virtualRow={virtualRow}
                                        onSelect={handleRowSelect}
                                    />
                                );
                            })}
                            {isFetchingMore &&
                                Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => {
                                    const offset = rowVirtualizer.getTotalSize() + index * ROW_HEIGHT;

                                    return (
                                        <div
                                            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton rows are static placeholders.
                                            key={`skeleton-${index}`}
                                            className="absolute left-0 top-0 w-full"
                                            style={{ transform: `translateY(${offset}px)` }}
                                        >
                                            <TableSkeletonRow
                                                columnCount={TABLE_COLUMNS.length}
                                                columnWidths={TABLE_COLUMNS}
                                            />
                                        </div>
                                    );
                                })}
                        </div>
                    )}
                </TableBody>
            </TableGrid>
        </TableContainer>
    );
};

const isStringArray = (value: unknown): value is readonly string[] => is.array(value, is.string);

const emptySelectedFilterValues: readonly string[] = [];

const getSelectedValues = (filterState: FilterState): readonly string[] =>
    isStringArray(filterState.value) ? filterState.value : emptySelectedFilterValues;

const getSelectedSkillIds = (filters: readonly FilterWithFilterStates[]): readonly string[] =>
    filters.flatMap((filterGroup) =>
        filterGroup.filterStates.flatMap((filterState) =>
            filterState.type === 'skill' ? getSelectedValues(filterState) : [],
        ),
    );

const hasDateValue = (filterState: FilterStateDate): boolean =>
    is.array(filterState.value) && filterState.value.some((value) => is.date(value));

const getFilterStateValueCount = (filterState: FilterState): number => {
    if (
        (filterState.type === 'string' ||
            filterState.type === 'integer' ||
            filterState.type === 'user' ||
            filterState.type === 'skill' ||
            filterState.type === 'uuid') &&
        isStringArray(filterState.value)
    ) {
        return filterState.value.length;
    }

    if (filterState.type === 'boolean') return is.boolean(filterState.value) ? 1 : 0;
    if (filterState.type === 'date') return hasDateValue(filterState) ? 1 : 0;
    if (filterState.type === 'percent') return is.array(filterState.value) ? 1 : 0;

    return 0;
};

const getActiveDynamicFilterCount = (filters: readonly FilterWithFilterStates[]): number =>
    filters.reduce(
        (filterCount, filterGroup) =>
            filterCount +
            filterGroup.filterStates.reduce(
                (stateCount, filterState) => stateCount + getFilterStateValueCount(filterState),
                0,
            ),
        0,
    );

const updateFilterStateValue = (
    filters: readonly FilterWithFilterStates[],
    field: string,
    value: FilterState['value'],
): readonly FilterWithFilterStates[] =>
    filters.map((filterGroup) => ({
        ...filterGroup,
        filterStates: filterGroup.filterStates.map((filterState) =>
            filterState.field === field ? ({ ...filterState, value } as FilterState) : filterState,
        ),
    })) as readonly FilterWithFilterStates[];

const toggleFilterStateValue = (
    filters: readonly FilterWithFilterStates[],
    filterState: FilterState,
    value: string,
): readonly FilterWithFilterStates[] => {
    const selectedValues = getSelectedValues(filterState);
    const nextValues = selectedValues.includes(value)
        ? selectedValues.filter((selectedValue) => selectedValue !== value)
        : [...selectedValues, value];

    return updateFilterStateValue(filters, filterState.field, nextValues.length > 0 ? nextValues : undefined);
};

const optionLabel = (options: readonly FilterStateOption[] | undefined, value: string): string =>
    options?.find((option) => option.id === value)?.label ?? value;

const formatInputDate = (value: Date | null | undefined): string => (is.date(value) ? format(value, 'yyyy-MM-dd') : '');

const parseInputDate = (value: string): Date | null =>
    is.nonEmptyString(value) ? new Date(`${value}T00:00:00.000Z`) : null;

const SubTriggerRow: FunctionComponent<{ readonly children: ReactNode; readonly count: number }> = ({
    children,
    count,
}) => (
    <DropdownMenuSubTrigger>
        <DropdownMenuSubTriggerContent>
            <span>{children}</span>
            {count > 0 && <Badge variant="orange">{count}</Badge>}
        </DropdownMenuSubTriggerContent>
        <IconChevronRightSmall size={14} />
    </DropdownMenuSubTrigger>
);

const OptionFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    if (!('options' in filterState) || is.emptyArray(filterState.options)) return null;

    const selectedValues = getSelectedValues(filterState);

    return (
        <div className="flex max-h-[32vh] min-w-[240px] flex-col gap-[2px]">
            {filterState.options.map((option) => (
                <DropdownMenuCheckboxItem
                    key={option.id}
                    checked={selectedValues.includes(option.id)}
                    onCheckedChange={() => onFiltersChange(toggleFilterStateValue(filters, filterState, option.id))}
                >
                    <span className="min-w-0 flex-1 truncate text-gray-900">{option.label}</span>
                </DropdownMenuCheckboxItem>
            ))}
        </div>
    );
};

const SkillFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
}> = ({ filterState, filters, onFiltersChange, onSkillLabelsChange }) => {
    const translate = useTranslate();
    const [searchValue, setSearchValue] = useState('');
    const { data: searchResults, isFetching } = useSearchSkillsQuery(searchValue, ['hard', 'soft', 'language'], 50, {
        allowEmptySearch: false,
    });
    const selectedValues = getSelectedValues(filterState);
    const { data: selectedSkills } = useManySkillsQuery({
        enabled: is.nonEmptyArray(selectedValues),
        skillIds: selectedValues,
    });
    const selectedIds = useMemo(() => new Set(selectedValues), [selectedValues]);
    const visibleSkills = useMemo(() => {
        const fetchedSkills = searchResults ?? [];
        const selectedSkillsById = new Map((selectedSkills ?? []).map((skill) => [skill.id, skill]));
        const fetchedIds = new Set(fetchedSkills.map((skill) => skill.id));
        const selectedFallbacks = selectedValues
            .filter((skillId) => fetchedIds.has(skillId) === false)
            .map((skillId) => selectedSkillsById.get(skillId) ?? { id: skillId, title: skillId });

        return [...selectedFallbacks, ...fetchedSkills];
    }, [searchResults, selectedSkills, selectedValues]);

    useEffect(() => {
        if (visibleSkills.length === 0) return;

        onSkillLabelsChange(Object.fromEntries(visibleSkills.map((skill) => [skill.id, skill.title])) as SkillLabelMap);
    }, [onSkillLabelsChange, visibleSkills]);

    return (
        <div className="flex min-w-[320px] flex-col gap-s">
            <div className="relative flex items-center gap-s border-b border-gray-400 px-s pb-s">
                <span className="flex items-center text-gray-700 opacity-50">
                    <IconSearch size={16} />
                </span>
                <Input
                    autoFocus={true}
                    className="!border-none !bg-transparent !p-0 !pl-s font-normal !text-gray-700 leading-[1.25rem]"
                    placeholder={translate('pages.job-architecture-v2.job-profile-skill-search-placeholder')}
                    value={searchValue}
                    onChange={(event) => setSearchValue(event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                />
                {isFetching && <Spinner style={{ position: 'absolute', right: '0.5rem' }} />}
            </div>
            <ScrollArea orientation="vertical">
                <div className="flex max-h-[32vh] flex-col gap-[2px]">
                    {visibleSkills.length === 0 && isFetching === false && (
                        <div className="px-m py-m text-center text-gray-500 leading-[1.25rem]">
                            {translate('pages.global-search.no-data.skills')}
                        </div>
                    )}
                    {visibleSkills.map((skill) => (
                        <DropdownMenuCheckboxItem
                            key={skill.id}
                            checked={selectedIds.has(skill.id)}
                            onCheckedChange={() =>
                                onFiltersChange(toggleFilterStateValue(filters, filterState, skill.id))
                            }
                        >
                            <span className="min-w-0 flex-1 truncate text-gray-900">{skill.title}</span>
                        </DropdownMenuCheckboxItem>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
};

const BooleanFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    const translate = useTranslate();
    if (filterState.type !== 'boolean') return null;

    return (
        <div className="flex min-w-[180px] flex-col gap-[2px]">
            {[
                { label: translate('common.yes'), value: true },
                { label: translate('common.no'), value: false },
            ].map((option) => (
                <DropdownMenuCheckboxItem
                    key={String(option.value)}
                    checked={filterState.value === option.value}
                    onCheckedChange={() =>
                        onFiltersChange(updateFilterStateValue(filters, filterState.field, option.value))
                    }
                >
                    {option.label}
                </DropdownMenuCheckboxItem>
            ))}
            {is.boolean(filterState.value) && (
                <DropdownMenuItem
                    onSelect={(event) => {
                        event.preventDefault();
                        onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined));
                    }}
                >
                    {translate('common.reset')}
                </DropdownMenuItem>
            )}
        </div>
    );
};

const DateFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    const translate = useTranslate();
    if (filterState.type !== 'date') return null;

    const [startValue, endValue] = is.array(filterState.value) ? filterState.value : [null, null];
    const updateDate = (index: 0 | 1, value: string): void => {
        const nextValue: [Date | null, Date | null] = [
            is.date(startValue) ? startValue : null,
            is.date(endValue) ? endValue : null,
        ];
        nextValue[index] = parseInputDate(value);
        onFiltersChange(
            updateFilterStateValue(
                filters,
                filterState.field,
                nextValue.some((date) => is.date(date)) ? nextValue : undefined,
            ),
        );
    };

    return (
        <div className="flex min-w-[280px] flex-col gap-s p-s">
            <label className="flex flex-col gap-xs text-gray-700 text-xxs" htmlFor={`${filterState.field}-start`}>
                {translate('common.start')}
                <Input
                    id={`${filterState.field}-start`}
                    type="date"
                    value={formatInputDate(is.date(startValue) ? startValue : null)}
                    onChange={(event) => updateDate(0, event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                />
            </label>
            <label className="flex flex-col gap-xs text-gray-700 text-xxs" htmlFor={`${filterState.field}-end`}>
                {translate('common.end')}
                <Input
                    id={`${filterState.field}-end`}
                    type="date"
                    value={formatInputDate(is.date(endValue) ? endValue : null)}
                    onChange={(event) => updateDate(1, event.target.value)}
                    onKeyDown={(event) => event.stopPropagation()}
                />
            </label>
        </div>
    );
};

const PercentFilterSubmenu: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
}> = ({ filterState, filters, onFiltersChange }) => {
    if (filterState.type !== 'percent') return null;

    const [startValue = 0, endValue = 1] = is.array(filterState.value) ? filterState.value : [0, 1];
    const updatePercent = (index: 0 | 1, value: string): void => {
        const numberValue = Math.max(0, Math.min(100, Number(value)));
        const nextValue: [number, number] = [startValue, endValue];
        nextValue[index] = numberValue / 100;
        onFiltersChange(updateFilterStateValue(filters, filterState.field, nextValue));
    };

    return (
        <div className="flex min-w-[240px] gap-s p-s">
            <Input
                aria-label={`${filterState.label} min`}
                max={100}
                min={0}
                type="number"
                value={Math.round(startValue * 100)}
                onChange={(event) => updatePercent(0, event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
            />
            <Input
                aria-label={`${filterState.label} max`}
                max={100}
                min={0}
                type="number"
                value={Math.round(endValue * 100)}
                onChange={(event) => updatePercent(1, event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
            />
        </div>
    );
};

const DynamicFilterSubmenuContent: FunctionComponent<{
    readonly filterState: FilterState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
}> = ({ filterState, filters, onFiltersChange, onSkillLabelsChange }) => {
    if (filterState.type === 'skill') {
        return (
            <SkillFilterSubmenu
                filterState={filterState}
                filters={filters}
                onFiltersChange={onFiltersChange}
                onSkillLabelsChange={onSkillLabelsChange}
            />
        );
    }

    if (filterState.type === 'boolean') {
        return <BooleanFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'date') {
        return <DateFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'percent') {
        return <PercentFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    if (filterState.type === 'string' || filterState.type === 'integer' || filterState.type === 'user') {
        return <OptionFilterSubmenu filterState={filterState} filters={filters} onFiltersChange={onFiltersChange} />;
    }

    return null;
};

const LearningOpportunityFiltersPopover: FunctionComponent<{
    readonly activeState: LearningOpportunityState;
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onResetDynamicFilters: () => void;
    readonly onSkillLabelsChange: (skillLabels: SkillLabelMap) => void;
    readonly onStateChange: (state: LearningOpportunityState) => void;
}> = ({ activeState, filters, onFiltersChange, onResetDynamicFilters, onSkillLabelsChange, onStateChange }) => {
    const translate = useTranslate();
    const dynamicFilterCount = getActiveDynamicFilterCount(filters);
    const activeCount = (activeState === 'all' ? 0 : 1) + dynamicFilterCount;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger>
                <Button iconPosition="trailing" isActive={activeCount > 0} variant="secondary">
                    {activeCount > 0
                        ? translate('pages.job-architecture-v2.skills.filter-selected-count', { count: activeCount })
                        : translate('pages.job-architecture-v2.skills.filter-button')}
                    <IconFilter size={12} />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
                <DropdownMenuSub>
                    <SubTriggerRow count={activeState === 'all' ? 0 : 1}>
                        {translate('pages.learning-opportunities.headlines.state')}
                    </SubTriggerRow>
                    <DropdownMenuSubContent>
                        {learningOpportunityStates.map((state) => (
                            <DropdownMenuCheckboxItem
                                key={state}
                                checked={activeState === state}
                                onCheckedChange={() => onStateChange(state)}
                            >
                                {translate(stateTranslationKey(state))}
                            </DropdownMenuCheckboxItem>
                        ))}
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
                {filters.map((filterGroup) =>
                    filterGroup.filterStates.map((filterState) => {
                        const valueCount = getFilterStateValueCount(filterState);
                        const content = (
                            <DynamicFilterSubmenuContent
                                filterState={filterState}
                                filters={filters}
                                onFiltersChange={onFiltersChange}
                                onSkillLabelsChange={onSkillLabelsChange}
                            />
                        );

                        if (content === null) return null;

                        return (
                            <DropdownMenuSub key={`${filterGroup.id}-${filterState.field}`}>
                                <SubTriggerRow count={valueCount}>{filterState.label}</SubTriggerRow>
                                <DropdownMenuSubContent>{content}</DropdownMenuSubContent>
                            </DropdownMenuSub>
                        );
                    }),
                )}
                {dynamicFilterCount > 0 && (
                    <DropdownMenuItem onSelect={onResetDynamicFilters}>{translate('common.reset')}</DropdownMenuItem>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

type LearningOpportunityActiveChip = {
    readonly key: string;
    readonly label: string;
    readonly onRemove: () => void;
    readonly value: string;
};

const buildActiveFilterChips = ({
    filters,
    onFiltersChange,
    skillLabels,
    translate,
}: {
    readonly filters: readonly FilterWithFilterStates[];
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly skillLabels: SkillLabelMap;
    readonly translate: (key: TranslationKey, values?: Record<string, unknown>) => string;
}): readonly LearningOpportunityActiveChip[] =>
    filters.flatMap((filterGroup) =>
        filterGroup.filterStates.flatMap(
            // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: each branch mirrors one backend filter-state variant.
            (filterState) => {
                if (filterState.type === 'boolean' && is.boolean(filterState.value)) {
                    return [
                        {
                            key: `${filterState.field}-${String(filterState.value)}`,
                            label: filterState.label,
                            onRemove: () =>
                                onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined)),
                            value: translate(filterState.value ? 'common.yes' : 'common.no'),
                        },
                    ];
                }

                if (filterState.type === 'date' && hasDateValue(filterState)) {
                    const [startValue, endValue] = filterState.value ?? [null, null];
                    const startLabel = is.date(startValue) ? format(startValue, 'yyyy-MM-dd') : '…';
                    const endLabel = is.date(endValue) ? format(endValue, 'yyyy-MM-dd') : '…';

                    return [
                        {
                            key: `${filterState.field}-date`,
                            label: filterState.label,
                            onRemove: () =>
                                onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined)),
                            value: `${startLabel} - ${endLabel}`,
                        },
                    ];
                }

                if (filterState.type === 'percent' && is.array(filterState.value)) {
                    const [startValue, endValue] = filterState.value;

                    return [
                        {
                            key: `${filterState.field}-percent`,
                            label: filterState.label,
                            onRemove: () =>
                                onFiltersChange(updateFilterStateValue(filters, filterState.field, undefined)),
                            value: `${Math.round(startValue * 100)}% - ${Math.round(endValue * 100)}%`,
                        },
                    ];
                }

                if (
                    filterState.type === 'string' ||
                    filterState.type === 'integer' ||
                    filterState.type === 'user' ||
                    filterState.type === 'skill' ||
                    filterState.type === 'uuid'
                ) {
                    return getSelectedValues(filterState).map((value) => ({
                        key: `${filterGroup.id}-${filterState.field}-${value}`,
                        label: filterState.label,
                        onRemove: () => onFiltersChange(toggleFilterStateValue(filters, filterState, value)),
                        value:
                            filterState.type === 'skill'
                                ? (skillLabels[value] ?? value)
                                : optionLabel('options' in filterState ? filterState.options : undefined, value),
                    }));
                }

                return [];
            },
        ),
    );

const LearningOpportunityActiveFilterChips: FunctionComponent<{
    readonly filters: readonly FilterWithFilterStates[];
    readonly skillLabels: SkillLabelMap;
    readonly state: LearningOpportunityState;
    readonly onFiltersChange: (filters: readonly FilterWithFilterStates[]) => void;
    readonly onStateChange: (state: LearningOpportunityState) => void;
}> = ({ filters, onFiltersChange, onStateChange, skillLabels, state }) => {
    const translate = useTranslate();
    const chips = buildActiveFilterChips({ filters, onFiltersChange, skillLabels, translate });

    if (state === 'all' && chips.length === 0) return null;

    return (
        <div className="flex flex-1 flex-wrap justify-end gap-s">
            {state !== 'all' && (
                <Badge variant="gray" onClick={() => onStateChange('all')}>
                    <span className="flex items-center gap-xs">
                        <span className="text-gray-600">
                            {translate('pages.learning-opportunities.headlines.state')}:
                        </span>
                        {translate(stateTranslationKey(state))}
                        <IconCrossSmall size={12} />
                    </span>
                </Badge>
            )}
            {chips.map((chip) => (
                <Tooltip key={chip.key}>
                    <Tooltip.Trigger asChild={true}>
                        <Badge variant="gray" onClick={chip.onRemove}>
                            <span className="flex items-center gap-xs">
                                <span className="text-gray-600">{chip.label}:</span>
                                <span className="text-gray-900">{chip.value}</span>
                                <IconCrossSmall size={12} />
                            </span>
                        </Badge>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{translate('common.remove')}</Tooltip.Content>
                </Tooltip>
            ))}
        </div>
    );
};

const LearningOpportunitiesMainTile: FunctionComponent = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const { canUserCreateLearningOpportunities } = useLearningOpportunitiesPermissions();
    const filterQuery = useLearningOpportunityFilter();
    const { inputValue, searchTerm, setInputValue } = useDebouncedSearch();
    const [state, setState] = useState<LearningOpportunityState>('all');
    const [skillLabels, setSkillLabels] = useState<SkillLabelMap>({});
    const [optimisticFilters, setOptimisticFilters] = useState<readonly FilterWithFilterStates[] | undefined>();
    const dynamicFilters = filterQuery.data?.filters ?? [];
    const currentFilters = optimisticFilters ?? dynamicFilters;
    const selectedSkillIds = useMemo(() => getSelectedSkillIds(currentFilters), [currentFilters]);
    const { data: selectedSkills } = useManySkillsQuery({
        enabled: is.nonEmptyArray(selectedSkillIds),
        skillIds: selectedSkillIds,
    });
    const dynamicFilter = useMemo(
        () => convertFilterStateToFilters(currentFilters.flatMap((filterGroup) => filterGroup.filterStates)),
        [currentFilters],
    );
    const updateSkillLabels = useCallback((nextSkillLabels: SkillLabelMap): void => {
        setSkillLabels((currentSkillLabels) => {
            const hasChanges = Object.entries(nextSkillLabels).some(
                ([skillId, label]) => currentSkillLabels[skillId] !== label,
            );

            return hasChanges ? { ...currentSkillLabels, ...nextSkillLabels } : currentSkillLabels;
        });
    }, []);
    const updateFilters = useCallback(
        (updatedFilters: readonly FilterWithFilterStates[]): void => {
            setOptimisticFilters(updatedFilters);
            filterQuery.data?.saveFilterValues(updatedFilters);
        },
        [filterQuery.data],
    );
    const resetFilters = useCallback((): void => {
        setOptimisticFilters(undefined);
        filterQuery.data?.resetFilterValues();
    }, [filterQuery.data]);
    // biome-ignore lint/correctness/useExhaustiveDependencies: the URL query string is the source of truth for persisted filters.
    useEffect(() => {
        setOptimisticFilters(undefined);
    }, [location.search]);
    useEffect(() => {
        if (is.emptyArray(selectedSkills ?? [])) return;

        updateSkillLabels(Object.fromEntries((selectedSkills ?? []).map((skill) => [skill.id, skill.title])));
    }, [selectedSkills, updateSkillLabels]);

    const header = (
        <BoardHeaderWrapper style={{ gap: 0 }}>
            <BoardHeaderRow style={{ flexShrink: 0 }}>
                <TileTitle>{translate('pages.learning-opportunities.headline')}</TileTitle>
                <RightSide>
                    <LearningOpportunityFiltersPopover
                        activeState={state}
                        filters={currentFilters}
                        onFiltersChange={updateFilters}
                        onResetDynamicFilters={resetFilters}
                        onSkillLabelsChange={updateSkillLabels}
                        onStateChange={setState}
                    />
                    <OutlinedInputWrapper>
                        <SearchInput
                            placeholder={translate('pages.learning-opportunities.toolbar.quick-search-placeholder')}
                            value={inputValue}
                            onChange={setInputValue}
                        />
                    </OutlinedInputWrapper>
                    {canUserCreateLearningOpportunities && (
                        <Button
                            iconPosition="leading"
                            variant="secondary"
                            onClick={() => navigate(route.patterns.createNewLearningOpportunity)}
                        >
                            <IconPlus size={14} />
                            {translate('pages.learning-opportunities.create-button')}
                        </Button>
                    )}
                </RightSide>
            </BoardHeaderRow>
            <div className="flex items-center justify-end gap-m pt-s pb-s">
                <LearningOpportunityActiveFilterChips
                    filters={currentFilters}
                    skillLabels={skillLabels}
                    state={state}
                    onFiltersChange={updateFilters}
                    onStateChange={setState}
                />
            </div>
        </BoardHeaderWrapper>
    );

    return (
        <BoardContentWithHeader
            content={<LearningOpportunitiesTable filter={dynamicFilter} searchTerm={searchTerm} state={state} />}
            header={header}
        />
    );
};

export const LearningManagementPage: FunctionComponent = () => {
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { openTile } = useUrlTiles();
    const { canUserSeeLearningOpportunities, isPending } = useLearningOpportunitiesPermissions();
    const opportunityId = params[route.params.id];
    const isCreateRoute = location.pathname.endsWith('/new');
    const isEditRoute = location.pathname.endsWith('/edit');
    const shouldShowSidepanel = isCreateRoute || is.nonEmptyString(opportunityId);
    const openSkillDetails = useCallback(
        (skillId: string, preferredLanguage?: string): void => {
            openTile(createSkillDetailTilePayload({ preferredLanguage, skillId }));
        },
        [openTile],
    );

    if (isPending) {
        return (
            <div className="p-xxl">
                <Skeleton height="40px" width="40%" />
            </div>
        );
    }

    if (!canUserSeeLearningOpportunities) {
        return <Error403 />;
    }

    return (
        <ThemeProvider theme={theme}>
            <ConfirmProvider>
                <LearningOpportunitiesMainTile />
                {shouldShowSidepanel && (
                    <TilingEngine.Portal id="learning-opportunity-details" maxWidth="50%" minWidth="50%">
                        <LearningOpportunitySidepanel
                            mode={isCreateRoute ? 'create' : isEditRoute ? 'edit' : 'view'}
                            opportunityId={opportunityId}
                            onSkillClick={openSkillDetails}
                            onClose={() => navigate(route.patterns.learningOpportunities)}
                            onTrainedSkillsChange={noop}
                        />
                    </TilingEngine.Portal>
                )}
            </ConfirmProvider>
        </ThemeProvider>
    );
};
